import { ProductSubCategory } from '@getethos/ethos-global-lib';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CarrierFactory } from '../../../../common/carrierConfig/carrierFactory';
import { Carriers } from '../../../../common/constants/carriers';
import { DocumentType } from '../../../../common/constants/documentType';
import { Resource } from '../../../../common/constants/resource';
import { NoraPermissionV2 } from '../../../../common/constants/roles';
import { TaskState } from '../../../../common/constants/states';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { useAccess } from '../../../../common/hooks/useAccess';
import { ITask } from '../../../tasks/interfaces/ITask';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import { useSelector } from '../../Detail';
import styles from '../../styles.module.scss';
import { FileDetailsItem } from '../components/FileDetailsItem';
import { FileUpload } from '../components/FileUpload';
import { TaskDetailsTitledItem } from '../components/TaskDetailsTitledItem';
import { useLetterDrawerHandle } from './drawer';
import LetterGenerator from './generator';
import { LETTER_DEFAULT_EMAIL } from './generator/constants';
import { LetterTypes } from './generator/types';
import {
  getLetterRecipientFullName,
  getProposedInsuredName,
} from './generator/utils';
import { LetterSendConfirmation } from './LetterSendConfirmation';

interface TaskStateData {
  taskId: number;
  state: TaskState;
  reason?: string;
}

const AutoSendingPlaceholder = () => {
  const [tick, setTick] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setTick(t => (t % 3) + 1), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      data-label="auto_sending_letter_placeholder"
      style={{ padding: '12px 16px', color: '#555', fontStyle: 'italic' }}
    >
      Generating and sending letter — please wait
      <span style={{ display: 'inline-block', minWidth: '1.5em' }}>
        {'.'.repeat(tick)}
      </span>
    </div>
  );
};

export const LetterDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteAttachmentHandler,
  sendEmailHandler,
  riders = [],
}: {
  task: ITask;
  riders?: string[];
  uploadHandler: (id: number, data: FormData, type: string) => void;
  sendEmailHandler: (taskId: number, reason?: string) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  deleteAttachmentHandler: (
    taskId: number,
    taskType: TaskTypes,
    attachmentId: number,
  ) => void;
}) => {
  const [confirmTaskStateData, setConfirmTaskStateData] =
    useState<TaskStateData | null>(null);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const carrierName = useSelector(state => state.application.carrier);
  const fullName = useSelector(state => state.application.customerName);
  const policyholderName = useSelector(
    state => state.application.policyholderName,
  );
  const policyNumber = useSelector(
    state => state.application.carrierPolicyNumber,
  );
  const subCategory = useSelector(state => state.application.subCategory);
  const isJuvenile = subCategory === ProductSubCategory.JUVENILE;
  const beneficiaryDetails = useSelector(
    state => state.application.beneficiaryDetails,
  );
  const proposedInsuredName = useMemo(
    () => getProposedInsuredName(isJuvenile, beneficiaryDetails),
    [isJuvenile, beneficiaryDetails],
  );
  const { flags } = useContext(FeatureFlagsContext);
  const {
    isLetterAutomationEnabled,
    isLetterAutomationSendingEnabled,
    uwLetterBuilderDrawerEnabled,
  } = flags;
  const isLegacyGeneratorEnabled =
    isLetterAutomationEnabled && !uwLetterBuilderDrawerEnabled;
  const isAutoSending = useSelector(state =>
    state.taskList.autoSendingTaskIds.includes(task.id),
  );

  const onFileSelection = (file: any, type: string) => {
    uploadHandler(task.id, file, type);
  };

  const letterType = useMemo(
    () => getLetterType(task.taskType),
    [task.taskType],
  );

  const carrier = useMemo(() => getCarrier(carrierName), [carrierName]);

  const canUploadLetter = useAccess({ permission: NoraPermissionV2.UPLOAD_LETTERS });

  const handleTaskState = useCallback(
    (taskId: number, state: TaskState, reason?: string) => {
      if (state === TaskState.COMPLETED && isLetterAutomationSendingEnabled) {
        setConfirmTaskStateData({ taskId, state, reason });
      } else {
        taskStateHandler(taskId, state, reason);
      }
    },
    [taskStateHandler, isLetterAutomationSendingEnabled],
  );

  const handleOnManualSend = useCallback(() => {
    if (!confirmTaskStateData) return;
    const { state, reason, taskId } = confirmTaskStateData;
    taskStateHandler(taskId, state, reason);
    setConfirmTaskStateData(null);
  }, [taskStateHandler, confirmTaskStateData]);

  const handleOnSend = useCallback(() => {
    if (!confirmTaskStateData) return;
    const { reason, taskId } = confirmTaskStateData;
    sendEmailHandler(taskId, reason);
    setConfirmTaskStateData(null);
  }, [sendEmailHandler, confirmTaskStateData]);

  const drawerHandle = useLetterDrawerHandle();
  const handleGenerateClick = useCallback(() => {
    if (uwLetterBuilderDrawerEnabled && drawerHandle && carrier && letterType) {
      if (drawerHandle.isOpen) {
        // Drawer already open — route through the guarded path to avoid
        // silently discarding in-progress edits (RP-7985).
        if (drawerHandle.letterType !== letterType) {
          drawerHandle.requestLetterType(letterType);
        }
        // Same letter type and already open: no-op (preserve edits).
        return;
      }
      drawerHandle.open({
        letterType,
        carrier,
        carrierDisplayName:
          CarrierFactory.getCarrierConfig(carrier)?.getCarrierDisplayName() ??
          '',
        policyNumber: policyNumber ?? '',
        recipientFullName: getLetterRecipientFullName(
          isJuvenile,
          policyholderName,
          fullName,
        ),
        proposedInsuredName: proposedInsuredName ?? '',
        riders,
        isJuvenile,
        onUpload: (files: File[]) =>
          onFileSelection(files, getDocumentType(task.taskType)),
      });
      return;
    }
    setIsGeneratorOpen(true);
  }, [
    uwLetterBuilderDrawerEnabled,
    drawerHandle,
    carrier,
    letterType,
    policyNumber,
    isJuvenile,
    policyholderName,
    fullName,
    proposedInsuredName,
    riders,
    task.taskType,
  ]);

  return (
    <TaskDetailsTitledItem
      task={task}
      title={
        task.taskType === TaskTypes.SPECIAL_AUTHORIZATION
          ? Resource.TASK_TITLE_SPECIAL_AUTHORIZATION
          : task.taskType === TaskTypes.RUW_WITHDRAWL_LETTER
            ? Resource.RUW_WITHDRAWL_LETTER
            : Resource.TASK_TITLE_LETTER
      }
      taskStateHandler={handleTaskState}
      dataLabel={
        task.taskType === TaskTypes.SPECIAL_AUTHORIZATION
          ? 'special_auth_section'
          : task.taskType === TaskTypes.RUW_WITHDRAWL_LETTER
            ? 'withdrawal_letter_section'
            : 'letter_section'
      }
      deleteAttachmentHandler={deleteAttachmentHandler}
    >
      {isLetterAutomationSendingEnabled && (
        <LetterSendConfirmation
          onSend={handleOnSend}
          onSendManually={handleOnManualSend}
          onClose={() => setConfirmTaskStateData(null)}
          show={!!confirmTaskStateData}
        />
      )}
      {isAutoSending ? (
        <AutoSendingPlaceholder />
      ) : (
        renderLetterTaskDetail(
          task,
          onFileSelection,
          downloadHandler,
          deleteAttachmentHandler,
          handleGenerateClick,
          isLetterAutomationEnabled,
          letterType,
          canUploadLetter,
        )
      )}
      {letterType &&
        carrier &&
        CarrierFactory.getCarrierConfig(carrier)?.supportLetterGeneration() &&
        policyNumber &&
        isLegacyGeneratorEnabled && (
          <LetterGenerator
            onSuccess={(file: File[]) =>
              onFileSelection(file, getDocumentType(task.taskType))
            }
            fullName={getLetterRecipientFullName(
              isJuvenile,
              policyholderName,
              fullName,
            )}
            carrier={carrier}
            initialLetterType={letterType}
            policyNumber={policyNumber}
            isOpen={isGeneratorOpen}
            onClose={() => setIsGeneratorOpen(false)}
            riders={riders}
            isJuvenile={isJuvenile}
            proposedInsuredName={proposedInsuredName}
            email={LETTER_DEFAULT_EMAIL}
          />
        )}
    </TaskDetailsTitledItem>
  );
};

const LETTER_UPLOAD_KEY = 'LETTER';

function renderLetterTaskDetail(
  task: ITask,
  onFileSelection: (file: any, type: string) => void,
  downloadHandler: (uri: string, fileName: string) => void,
  deleteAttachmentHandler: (
    taskId: number,
    taskType: TaskTypes,
    attachmentId: number,
  ) => void,
  onGenerateClick: () => void,
  isLetterAutomationEnabled: boolean,
  letterType: LetterTypes | null,
  canUploadLetter: boolean,
) {
  if (task.attachment) {
    const attachment = task.attachment;
    return (
      <FileDetailsItem
        canDelete
        fileDeletehandler={documentId =>
          deleteAttachmentHandler(task.id, task.taskType, documentId)
        }
        key={attachment.uri}
        leftItem={getResource(task.taskType).toString()}
        document={attachment}
        fileOpenHandler={downloadHandler}
      />
    );
  }

  if (!canUploadLetter) {
    return null;
  }

  return (
    <div className={styles.ButtonsContainer}>
      <FileUpload
        leftItem={getResource(task.taskType).toString()}
        selectedFiles={onFileSelection}
        type={getDocumentType(task.taskType)}
        multiple={false}
        ongoingUpload={task.ongoingUpload?.[LETTER_UPLOAD_KEY]}
      />
      {isLetterAutomationEnabled && letterType && (
        <div className={styles.GenerateButton} onClick={onGenerateClick}>
          Generate
        </div>
      )}
    </div>
  );
}

const getLetterType = (type: TaskTypes): LetterTypes | null => {
  if (type === TaskTypes.OTAF_LETTER) return LetterTypes.otaf;
  if (type === TaskTypes.DECLINE_LETTER) return LetterTypes.decline;
  if (type === TaskTypes.RUW_WITHDRAWL_LETTER) return LetterTypes.withdrawal;
  if (type === TaskTypes.SUBSTANDARD_LETTER) return LetterTypes.substandard;
  if (type === TaskTypes.REINSTATEMENT_APPROVAL_LETTER)
    return LetterTypes.reinstatementApproval;
  if (type === TaskTypes.REINSTATEMENT_DECLINE_LETTER)
    return LetterTypes.reinstatementDecline;
  if (type === TaskTypes.REINSTATEMENT_WITHDRAWN_LETTER)
    return LetterTypes.reinstatementWithdrawal;
  return null;
};

const getCarrier = (carrierName: string): Carriers | null => {
  return CarrierFactory.getCarrierConfig(carrierName)?.getCarrier() ?? null;
};

const getResource = (type: TaskTypes): Resource => {
  if (type === TaskTypes.OTAF_LETTER) return Resource.OTAF_LETTER;
  if (type === TaskTypes.SUBSTANDARD_LETTER) return Resource.SUBSTANDARD_LETTER;
  if (type === TaskTypes.SPECIAL_AUTHORIZATION)
    return Resource.TASK_TITLE_SPECIAL_AUTHORIZATION;
  if (type === TaskTypes.REINSTATEMENT_APPROVAL_LETTER)
    return Resource.LETTER_OF_REINSTATEMENT;
  if (type === TaskTypes.REINSTATEMENT_DECLINE_LETTER)
    return Resource.REINSTATEMENT_DECLINE_LETTER;
  if (type === TaskTypes.REINSTATEMENT_WITHDRAWN_LETTER)
    return Resource.REINSTATEMENT_WITHDRAWN_LETTER;
  if (type === TaskTypes.RUW_WITHDRAWL_LETTER)
    return Resource.RUW_WITHDRAWL_LETTER;
  return Resource.DECLINE_LETTER;
};

export const getDocumentType = (type: TaskTypes): DocumentType => {
  if (type === TaskTypes.OTAF_LETTER) return DocumentType.OTAF_LETTER;
  if (type === TaskTypes.SUBSTANDARD_LETTER)
    return DocumentType.SUBSTANDARD_LETTER;
  if (type === TaskTypes.SPECIAL_AUTHORIZATION)
    return DocumentType.SPECIAL_AUTHORIZATION;
  if (type === TaskTypes.REINSTATEMENT_APPROVAL_LETTER)
    return DocumentType.REINSTATEMENT_APPROVAL_LETTER;
  if (type === TaskTypes.REINSTATEMENT_DECLINE_LETTER)
    return DocumentType.REINSTATEMENT_DECLINE_LETTER;
  if (type === TaskTypes.REINSTATEMENT_WITHDRAWN_LETTER)
    return DocumentType.REINSTATEMENT_WITHDRAWN_LETTER;
  if (type === TaskTypes.REINSTATEMENT_APPLICATION)
    return DocumentType.REINSTATEMENT_APPLICATION;
  if (type === TaskTypes.RUW_WITHDRAWL_LETTER)
    return DocumentType.RUW_WITHDRAWL_LETTER;
  return DocumentType.DECLINE_LETTER;
};
